import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, Shield, CheckCircle2, Loader2, GraduationCap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PhishCaughtPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acked, setAcked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/phishpass/landing/${token}`);
        setData(r.data);
      } catch (e) {
        setData({ error: 'Invalid or expired link' });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAck = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/phishpass/acknowledge/${token}`);
      setAcked(true);
    } catch (e) {
      // ignore
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  if (data?.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <Shield className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <h1 className="font-semibold text-slate-900">Link not recognised</h1>
            <p className="text-sm text-slate-500 mt-1">This phishing-simulation link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const alreadyAcked = acked || data?.already_acknowledged;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-amber-50 to-yellow-50 py-10 px-4" data-testid="phish-caught-page">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-6">
          <div className="inline-block bg-red-500 text-white rounded-full p-3 mb-3 animate-pulse">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900" data-testid="caught-headline">🎣 You&apos;ve been phished!</h1>
          <p className="text-slate-600 mt-2">Don&apos;t worry — this was a <b>simulation</b> from your IT Security team. No harm done.</p>
        </div>

        {/* Scenario card */}
        <Card className="mb-4 border-amber-200" data-testid="scenario-card">
          <CardContent className="p-5">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">The email you clicked</div>
            <div className="text-lg font-semibold text-slate-900">{data.template_name}</div>
            <div className="text-sm text-slate-600 mt-1">Subject: <span className="italic">&quot;{data.subject}&quot;</span></div>
            <div className="text-xs text-slate-500 mt-1">Type: {data.scenario_type}</div>
            {data.landing_message && (
              <div className="mt-3 bg-blue-50 border-l-4 border-blue-400 px-3 py-2 text-sm text-blue-900">
                💡 {data.landing_message}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Red flags */}
        {data.red_flags && data.red_flags.length > 0 && (
          <Card className="mb-4" data-testid="red-flags-card">
            <CardContent className="p-5">
              <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Red flags you should&apos;ve spotted
              </h2>
              <ul className="space-y-2">
                {data.red_flags.map((flag, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <div className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</div>
                    <span className="text-slate-700">{flag}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="mb-4">
          <CardContent className="p-5">
            <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              Before you click next time…
            </h2>
            <ul className="space-y-2 text-sm text-slate-700">
              <li>• <b>Hover over links</b> before clicking — does the URL match the sender&apos;s domain?</li>
              <li>• <b>Verify urgency</b> — real IT/HR rarely demand same-day action via email.</li>
              <li>• <b>Type known URLs directly</b> (office.com, your HR portal) instead of clicking.</li>
              <li>• <b>Confirm by phone or in person</b> for anything financial, especially from senior leaders.</li>
              <li>• <b>When in doubt, forward to IT</b> before doing anything else.</li>
            </ul>
          </CardContent>
        </Card>

        {/* Acknowledge */}
        {alreadyAcked ? (
          <div className="text-center bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="ack-confirmation">
            <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <div className="font-semibold text-emerald-800">Thanks — your acknowledgement is recorded.</div>
            <div className="text-sm text-emerald-700 mt-1">You can safely close this window.</div>
          </div>
        ) : (
          <div className="text-center">
            <Button onClick={handleAck} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ack-btn">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Got it — I&apos;ll be careful next time
            </Button>
          </div>
        )}

        <div className="text-center text-xs text-slate-400 mt-6">
          Harmony PhishPass · Internal Security Awareness Training
        </div>
      </div>
    </div>
  );
};

export default PhishCaughtPage;
